import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Home, Mail, Phone, Linkedin, Twitter, Instagram, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CursorGlow from '../components/CursorGlow';

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'contact';

  const messages = {
    contact: {
      title: 'Mesajınız Alındı!',
      description: 'İletişim formunuzu aldık. Ekibimiz en kısa sürede sizinle iletişime geçecek.',
      time: '24 saat içinde',
      icon: Mail
    },
    quote: {
      title: 'Teklif Talebiniz Alındı!',
      description: 'Proje detaylarınızı inceledik. Size özel bir teklif hazırlayıp en kısa sürede ileteceğiz.',
      time: '48 saat içinde',
      icon: CheckCircle
    },
    analysis: {
      title: 'Analiz Talebiniz Alındı!',
      description: 'Web sitenizin ücretsiz SEO analizini hazırlıyoruz. Detaylı raporu e-posta ile göndereceğiz.',
      time: '24-48 saat içinde',
      icon: CheckCircle
    },
    newsletter: {
      title: 'Hoş Geldiniz!',
      description: 'E-bültenimize başarıyla kaydoldunuz. En güncel içeriklerimizi e-posta ile alacaksınız.',
      time: 'Hemen',
      icon: Mail
    }
  };

  const currentMessage = messages[type] || messages.contact;
  const Icon = currentMessage.icon;

  const nextSteps = [
    { title: 'Hizmetlerimizi Keşfedin', description: 'Dijital çözümlerimize göz atın', link: '/?scrollTo=services' },
    { title: 'Blog Yazılarımız', description: 'Teknoloji ve dijital pazarlama ipuçları', link: '/blog' },
    { title: 'Başarı Hikayelerimiz', description: 'Müşterilerimizin başarı öyküleri', link: '/calismaalarimiz' }
  ];

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:text-blue-400' },
    { icon: Twitter, href: '#', label: 'Twitter', color: 'hover:text-sky-400' },
    { icon: Instagram, href: '#', label: 'Instagram', color: 'hover:text-pink-400' }
  ];

  useEffect(() => {
    // Analytics tracking
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-CONVERSION_ID',
        'transaction_id': ''
      });
    }
  }, [type]);

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <Header />

      <section className="relative pt-32 pb-20 overflow-hidden min-h-[80vh] flex items-center">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-1/4 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-1/4 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500/50 rounded-full blur-2xl animate-pulse"></div>
              <div className="relative w-24 h-24 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center">
                <Icon className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {currentMessage.title}
          </h1>

          <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto">
            {currentMessage.description}
          </p>

          <div className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-12">
            <span className="text-cyan-400 font-semibold">Beklenen Süre:</span>
            <span className="text-white">{currentMessage.time}</span>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-white mb-6">Acil mi? Hemen Ulaşın</h3>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="mailto:info@ruyan.com.tr" className="flex items-center gap-2 text-gray-300 hover:text-cyan-400 transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@ruyan.com.tr</span>
              </a>
              <a href="tel:+90XXXXXXXXX" className="flex items-center gap-2 text-gray-300 hover:text-cyan-400 transition-colors">
                <Phone className="w-5 h-5" />
                <span>+90 XXX XXX XX XX</span>
              </a>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-8">Sırada Ne Var?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {nextSteps.map((step, index) => (
                <Link
                  key={index}
                  to={step.link}
                  className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:-translate-y-1"
                >
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {step.title}
                  </h4>
                  <p className="text-gray-400 text-sm mb-4">{step.description}</p>
                  <div className="flex items-center text-cyan-400 text-sm font-semibold">
                    Keşfet
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Social Media */}
          <div className="mb-12">
            <h3 className="text-xl font-bold text-white mb-6">Bizi Takip Edin</h3>
            <div className="flex justify-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-12 h-12 rounded-full bg-slate-800/50 border border-slate-700/50 flex items-center justify-center text-gray-400 transition-all duration-300 ${social.color} hover:scale-110 hover:border-cyan-500/50`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* CTA */}
          <Link to="/">
            <Button className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg group">
              <Home className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
              Ana Sayfaya Dön
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ThankYou;
